import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo } from 'react';

import { CloseIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, Popup, useClosePopupOnNavigation } from '@gem/popups';
import { customScrollStyles, IconButton } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { useProfileView } from 'APP/main/ChatView/hooks/profileView/useProfileView';

import {
  IUserProfilePopupParams,
  useUserProfilePopupPresenter,
} from './UserProfilePopup.presenter';
import styles from './UserProfilePopup.styles.m.css';

export const UserProfilePopup: FC<ICommonPopupsProps<IUserProfilePopupParams>> = observer(
  (props) => {
    const { t } = useTranslation();
    const presenter = useUserProfilePopupPresenter(props);
    const popupInstance = props.popupInstance;
    const { userId, initiatorSource, hasBackButton } = props.params!;
    const profileView = useProfileView({
      userId,
      initiatorSource,
      groupId: presenter.groupId,
      onClose: presenter.onClose,
    });
    useClosePopupOnNavigation(props.popupInstance);

    const childProps = useMemo(() => {
      if (profileView.isProfileOpened) {
        return {
          ...profileView.props,
          className: styles.profile,
          metaBlockClassName: styles.profileMetaBlock,
          userId,
          popupInstance,
          hasActions: true,
          onStartCall: presenter.onClose,
        };
      }

      if (profileView.isSharedDataOpened) {
        return {
          ...profileView.props,
          className: styles.sharedData,
        };
      }

      return profileView.props;
    }, [profileView.isSharedDataOpened]);

    useEffect(() => {
      if (!profileView.user) {
        Tasks.app.showAlert(t('deleted_user_profile'));
        presenter.onClose();
      }
    }, [profileView.user]);

    return (
      <Popup
        className={styles.container}
        title={profileView.isProfileOpened ? '' : profileView.title}
        isOpened={!!profileView.user}
        isHidden={popupInstance.isHidden}
        onClose={profileView.close}
        handlePrevStep={
          profileView.isProfileOpened
            ? hasBackButton
              ? presenter.onBack
              : undefined
            : profileView.goBack
        }
      >
        <div className={classNames(styles.scrollContainer, customScrollStyles.container)}>
          {!hasBackButton && profileView.isProfileOpened && (
            <IconButton
              Icon={CloseIcon}
              onClick={profileView.close}
              className={styles.close}
              data-test="close-popup"
            />
          )}
          <profileView.Component {...childProps} />
        </div>
      </Popup>
    );
  }
);
